import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiAccountApprovalEvent, ApiAssetPriceWithHistory, ApiTradeRejection } from 'api/rtk-api'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import type { RootState } from 'state/store'
import { UserRole, UserType } from 'types/user'

import Api from '@/api/_legacy'
import {
  ApiConfirmSettlementRequest,
  ApiNotePrices,
  ApiTransferOfUnits
} from '@/api/_legacy/generated-api'
import { parseApproveErrors } from '@/modules/trade-approval/util/validator'
import { TENANT_ID } from '@/modules/tradebuilder-v2/constants'
import { FundingSource } from '@/modules/tradebuilder-v2/modules/02-terms/state/terms-slice'
import { TradeActivationStep } from '@/modules/tradebuilder-v2/types'
import { Errors, truncate } from '@/modules/tradebuilder-v2/util'
import { Currency } from '@/utilities/currency'
import { createLogoutExtraReducers } from '@/utilities/redux'

export type TransferAsset = {
  actualWeight: number
  baseValue: number
  id: string
  name: string
  originalPurchasePrice: number
  price: number
  targetWeight: number
  units: number
  value: number
}

export type TradeSettlementStepState = {
  accountId: string
  approvalEvents: ApiAccountApprovalEvent[]
  assetPrices: Record<string, number>
  assetsToTransfer: TransferAsset[]
  baseCostPerUnit: number
  basePortfolioValue: number
  canProceed: boolean
  currency: Currency
  currentPortfolioValue: number
  errors: Errors
  generalErrors?: []
  hasCompleted: boolean
  investment: number
  isTransfer: boolean
  notePrice: number
  noteUnits: number
  rejectSuccess?: boolean
  success?: boolean
  userName: string
  userRoles: UserRole[]
  userType: UserType
}

const initialState: TradeSettlementStepState = {
  assetsToTransfer: [],
  assetPrices: {},
  baseCostPerUnit: 0,
  basePortfolioValue: 0,
  currentPortfolioValue: 0,
  canProceed: true,
  errors: {},
  generalErrors: [],
  approvalEvents: [],
  accountId: undefined,
  investment: 0,
  isTransfer: false,
  currency: undefined,
  notePrice: 0,
  noteUnits: 0,
  userName: undefined,
  hasCompleted: false,
  userType: 'admin',
  userRoles: []
}

export type TradeSettlementFormState = {
  accountId: string
  approvalEvents: ApiAccountApprovalEvent[]
  assetsToTransfer: TransferAsset[]
  currency: Currency
  investment: number
  isTransfer: boolean
  status: TradeActivationStep
  userName: string
  userRoles: UserRole[]
  userType: UserType
}

function updateTransferValuesAndWeights(
  transfers: TransferAsset[],
  prices: Record<string, number>
) {
  if (transfers.length === 0) return transfers
  transfers.forEach(transfer => {
    transfer.value = (transfer.units * prices[transfer.id]) / 100 // TODO - get price scaling factor from server
    transfer.baseValue = (transfer.units * transfer.originalPurchasePrice) / 100 // TODO - get price scaling factor from server
    transfer.price = prices[transfer.id]
  })
  const totalPortfolioValue = transfers.map(transfer => transfer.value).reduce((a, b) => a + b, 0)

  transfers.forEach(transfer => {
    transfer.actualWeight = totalPortfolioValue === 0 ? 0 : transfer.value / totalPortfolioValue
  })
  return transfers
}

export const tradeSettlementSlice = createSlice({
  name: 'tradeSettlementSlice',
  initialState,
  reducers: {
    reset: state => {
      state.canProceed = true
      state.hasCompleted = false
      state.success = undefined
      state.errors = {}
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    initialise: (state, action: PayloadAction<TradeSettlementFormState>) => {
      state.approvalEvents = action.payload.approvalEvents
        ? action.payload.approvalEvents.filter(x => x.statusBefore === 'PendingSettlement')
        : []
      state.assetsToTransfer = action.payload.assetsToTransfer
      state.hasCompleted = action.payload.status > TradeActivationStep.settlement
      state.accountId = action.payload.accountId
      state.investment = action.payload.investment
      state.isTransfer = action.payload.isTransfer
      state.currency = action.payload.currency
      state.userName = action.payload.userName
      state.userType = action.payload.userType
      state.userRoles = action.payload.userRoles
      state.success = false
    },
    fetchAssetPricesSuccess: (state, action: PayloadAction<ApiAssetPriceWithHistory[]>) => {
      action.payload.forEach(assetPriceEntry => {
        state.assetPrices[assetPriceEntry.isinCode] = assetPriceEntry.currentPrice?.price || 0
      })
      state.assetsToTransfer = updateTransferValuesAndWeights(
        state.assetsToTransfer,
        state.assetPrices
      )
    },
    fetchNoteDataSuccess: (state, action: PayloadAction<ApiNotePrices>) => {
      state.baseCostPerUnit = action.payload.baseCostPerUnit
      state.basePortfolioValue = action.payload.basePortfolioValue
      state.currentPortfolioValue = action.payload.currentPortfolioValue
      state.notePrice = action.payload.notePrice
      state.noteUnits = action.payload.noteUnits
    },
    postTradeApproveSuccess: state => {
      state.errors = {}
      state.success = true
    },
    postTradeRejectSuccess: state => {
      state.errors = {}
      state.rejectSuccess = true
    },
    postTradeApproveFailure: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    setAssetUnits: (state, action: PayloadAction<{ assetId: string; units: number }>) => {
      const i = state.assetsToTransfer.findIndex(asset => asset.id === action.payload.assetId)
      state.assetsToTransfer[i].units = action.payload.units
      state.assetsToTransfer = updateTransferValuesAndWeights(
        state.assetsToTransfer,
        state.assetPrices
      )
    },
    setOriginalPurchasePrice: (
      state,
      action: PayloadAction<{ assetId: string; price: number }>
    ) => {
      const i = state.assetsToTransfer.findIndex(asset => asset.id === action.payload.assetId)
      state.assetsToTransfer[i].originalPurchasePrice = truncate(action.payload.price, 2)
      state.assetsToTransfer = updateTransferValuesAndWeights(
        state.assetsToTransfer,
        state.assetPrices
      )
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.keys(logoutReducers).forEach(key => {
      builder.addCase(key, logoutReducers[key])
    })
  }
})

export const actions = { ...tradeSettlementSlice.actions }

export const postSettlementApprove =
  (accountId: string, request: ApiConfirmSettlementRequest) => async dispatch => {
    try {
      await Api.tenant.confirmCreate(accountId, TENANT_ID, request, { format: 'json' })
      dispatch(actions.postTradeApproveSuccess())
    } catch (response) {
      const errors: Errors = parseApproveErrors(response)
      dispatch(actions.postTradeApproveFailure(errors))
    }
  }
export const postSettlementReject = (accountId: string, comments: string) => async dispatch => {
  try {
    const request: ApiTradeRejection = {
      comments
    }
    await Api.tenant.rejectCreate(accountId, TENANT_ID, request, { format: 'json' })
    dispatch(actions.postTradeRejectSuccess())
  } catch (response) {
    const errors: Errors = parseApproveErrors(response)
    dispatch(actions.postTradeApproveFailure(errors))
  }
}

function* fetchLatestAssetPricesSaga() {
  try {
    const result = yield call(Api.tenant.latestAssetspricesDetail, TENANT_ID)
    yield put({ type: 'tradeSettlementSlice/fetchAssetPricesSuccess', payload: result.data })
  } catch (error) {
    yield put({
      type: 'tradeSettlementSlice/fetchAssetPricesFailure',
      payload: error?.message ?? String(error)
    })
  }
}

function* updateNotePriceDataSaga() {
  const state: RootState = yield select()
  const { accountId } = state.builder
  const { fundingSource } = state.terms.terms
  if (fundingSource === FundingSource.Transfer) {
    const { assetsToTransfer } = state.tradeSettlement
    const transferUnits: ApiTransferOfUnits[] = assetsToTransfer.map(asset => ({
      assetId: asset.id,
      units: asset.units,
      originalPurchasePrice: asset.originalPurchasePrice / 100 // TODO - get price scaling factor from server
    }))
    try {
      const result = yield call(Api.tenant.pricesCreate, accountId, TENANT_ID, transferUnits)
      yield put({ type: 'tradeSettlementSlice/fetchNoteDataSuccess', payload: result.data })
    } catch (error) {
      yield put({ type: 'tradeSettlementSlice/fetchNoteDataFailure', payload: error })
    }
  }
}

export function* initialiseSettlementSaga() {
  yield all([
    takeLatest('tradeSettlementSlice/setAssetUnits', updateNotePriceDataSaga),
    takeLatest('tradeSettlementSlice/setOriginalPurchasePrice', updateNotePriceDataSaga),
    takeLatest('tradeSettlementSlice/initialise', fetchLatestAssetPricesSaga),
    takeLatest('tradeSettlementSlice/fetchAssetPricesSuccess', updateNotePriceDataSaga)
  ])
}

export type TradeSettlementActions = typeof actions

export default tradeSettlementSlice.reducer
